/**
 * FamilyTreePageSearch — in-page search box that filters the current
 * tree's members by name OR place and shows up to 15 suggestions.
 * Each suggestion exposes two actions:
 *   • "Open profile"  → onPickMemberProfile(memberId)
 *   • "On tree"       → onPickMember(memberId) (locate on canvas)
 *
 * Extracted from FamilyTreeDetail.jsx so the ~5500-line page stays
 * maintainable. Self-contained — only depends on AncestorAvatar.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import AncestorAvatar from "@/components/AncestorAvatar";

export default function FamilyTreePageSearch({ members, treeSlug, onPickMember, onPickMemberProfile }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return (members || []).filter((m) => {
      const name = [m.first_name, m.middle_name, m.last_name, m.nickname]
        .filter(Boolean).join(" ").toLowerCase();
      const loc = [m.birth_place, m.current_location, m.location, m.origin_location]
        .filter(Boolean).join(" ").toLowerCase();
      return name.includes(s) || loc.includes(s);
    }).slice(0, 15);
  }, [q, members]);

  return (
    <div
      ref={boxRef}
      className="mb-4 relative"
      data-testid="ft-page-search"
    >
      <div
        className="flex items-center gap-2 rounded-full border pl-4 pr-2 py-2 bg-white shadow-sm"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <svg className="w-4 h-4 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search this family tree — name or place (e.g. 'Kurian' or 'Kottayam')"
          className="flex-1 bg-transparent outline-none text-sm"
          data-testid="ft-page-search-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(false); }}
            className="text-xs opacity-60 hover:opacity-100 px-2"
            data-testid="ft-page-search-clear"
          >
            Clear
          </button>
        )}
      </div>
      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-40 rounded-xl border bg-white shadow-xl overflow-hidden max-h-96 overflow-y-auto"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="ft-page-search-results"
        >
          {results.length === 0 ? (
            <div className="px-4 py-3 text-xs opacity-60">No matches. Try a different name or place.</div>
          ) : (
            results.map((m) => {
              const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
              const loc = m.current_location || m.birth_place || m.location || "";
              const yr = (m.birth_date || "").slice(0, 4);
              return (
                <div
                  key={m.id}
                  className="w-full text-left px-3 py-2 hover:bg-orange-50 flex items-center gap-3 border-b last:border-0"
                  style={{ borderColor: "var(--wofy-line)" }}
                  data-testid={`ft-page-search-result-${m.id}`}
                >
                  <button
                    type="button"
                    onClick={() => { setQ(""); setOpen(false); onPickMemberProfile(m.id); }}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    data-testid={`ft-page-search-result-profile-${m.id}`}
                    aria-label={`Open profile for ${nm || 'member'}`}
                  >
                    <AncestorAvatar member={m} size={32} showAddChip={false} className="!rounded-full shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold truncate">{nm || "—"}</span>
                      {loc && <span className="block text-[11px] opacity-70 truncate">📍 {loc}</span>}
                    </span>
                    {yr && <span className="text-xs opacity-50 tabular-nums">{yr}</span>}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQ(""); setOpen(false); onPickMember(m.id); }}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border shrink-0 hover:bg-orange-100"
                    style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-primary)" }}
                    data-testid={`ft-page-search-result-tree-${m.id}`}
                    title="Locate on tree"
                    aria-label={`Locate ${nm || 'member'} on the tree`}
                  >
                    On tree
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
